// @flow

import { createStore, type Store, applyMiddleware, compose } from 'redux';
import getViewportCategory, {
  RESPONSIVE_EVENTS_RADIO,
  RESPONSIVE_VIEWPORT_EVENT,
} from '@ta/common.responsive';
import throttle from '@ta/common.throttle';
import Radio from '@ta/common.radio';
import { featureIsEnabled } from '@ta/common.features';

// eslint-disable-next-line import/no-cycle
import reducer, {
  type State,
  type Action,
  PAUSE_REQUEST_ACTION,
  RESUME_REQUEST_ACTION,
  batchAction,
} from './state';
import { viewportCategoryChange } from './meta';

let currentStore = null;

export const getStore = (): Store<State, Action> => {
  if (currentStore) {
    return currentStore;
  }
  throw new Error('Attempted to access Redux store before initialization');
};

export default (state: $Shape<State>) => {
  let enhancer =
    (global.__REDUX_DEVTOOLS_EXTENSION__ && global.__REDUX_DEVTOOLS_EXTENSION__()) || undefined;

  if (featureIsEnabled('hydrate_while_loading')) {
    // This is feature gated for now to make sure we can turn it off if something unexpected happens

    let pausedActions: Array<Action> = [];
    let pauseCount = 0;

    // Process pause and resume actions to prevent dispatch before hydration is complete

    const middleware = () => next => action => {
      if (action?.type === PAUSE_REQUEST_ACTION) {
        pauseCount += 1;
        return action;
      }
      if (pauseCount > 0) {
        if (action?.type !== RESUME_REQUEST_ACTION) {
          pausedActions.push(action);
          return action;
        }
        pauseCount -= 1;
        if (pauseCount > 0 || !pausedActions.length) {
          return action;
        }
        next(batchAction(pausedActions));
        pausedActions = [];
        return action;
      }
      return next(action);
    };

    enhancer = enhancer
      ? compose(
          applyMiddleware(middleware),
          enhancer,
        )
      : applyMiddleware(middleware);
  }

  const store: Store<State, Action> = createStore(reducer, state, enhancer);

  // We make the singleton store available globally for non-React entities.
  global.__REDUX_STORE__ = store;
  currentStore = store;

  /* eslint-disable no-undef */
  // hack to expose our store to legacy AMD modules if AMD is ready
  if (!SERVER) {
    try {
      // $FlowFixMe
      define('redux-init', [], () => store);
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }
  /* eslint-enable no-undef */
  return store;
};

/** Functionality to update meta store viewportCategory when screen size changes */
const THROTTLE_RESIZE_DELAY = 250;
export const resizeHandler = () => {
  if (!global.__REDUX_STORE__) {
    return;
  }
  const store = getStore();
  // eslint-disable-next-line ta-code-conventions/func-get-viewport-category
  const calculatedViewportCategory = getViewportCategory();
  const currentViewportCategory = store.getState().meta.device.viewportCategory;
  if (calculatedViewportCategory !== currentViewportCategory) {
    store.dispatch(viewportCategoryChange(calculatedViewportCategory));
    Radio(RESPONSIVE_EVENTS_RADIO).emit(RESPONSIVE_VIEWPORT_EVENT, calculatedViewportCategory);
  }
};
const throttledResizeHandler = throttle(resizeHandler, THROTTLE_RESIZE_DELAY);

// eslint-disable-next-line no-restricted-globals
if (!SERVER && typeof window !== 'undefined') {
  // eslint-disable-next-line no-restricted-globals
  window.addEventListener('resize', throttledResizeHandler);
}
